import { Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { BaseComponent } from '../../base.component';
import { MultiDocField, MultiDocGroup } from '../../../models/field/multi-doc-field.model';
import { expand, map } from 'rxjs/operators';
import { CodingFieldsService } from '../../../services/coding-fields.service';
import { EMPTY, Observable } from 'rxjs';
import { PagingResponse } from '../../../models/paging-response.model';
import { DocViewerContext, DocViewerContextDoc } from '../../../models/ui/doc-viewer-context-doc.model';
import { EmailType } from '../../../models/email-type.model';

@Component({
    selector: 'lex-ai-review',
    templateUrl: './ai-review.component.html',
    styleUrls: ['./ai-review.component.scss'],
    standalone: false
})
export class AiReviewComponent extends BaseComponent {
	@ViewChild("focusFix") focusFix: ElementRef;

	public selectedCodingFieldId: string = '';
	public codingFieldGroups: MultiDocGroup[] = [];
	public isLoading: boolean = false;
	public progress: number = 0;

	public truePositive: DocViewerContextDoc[] = [];
	public falsePositive: DocViewerContextDoc[] = [];
	public trueNegative: DocViewerContextDoc[] = [];
	public falseNegative: DocViewerContextDoc[] = [];

	public markedAsResponsiveHuman: DocViewerContextDoc[] = [];
	public markedAsResponsiveAI: DocViewerContextDoc[] = [];
	public markedAsNonResponsiveHuman: DocViewerContextDoc[] = [];
	public markedAsNonResponsiveAI: DocViewerContextDoc[] = [];
	public notCodedHuman: DocViewerContextDoc[] = [];
	public notCodedAI: DocViewerContextDoc[] = [];

	public confidenceList = [
		{ confidenceLevel: 1, responsiveDoc: [], nonResponsiveDoc: [] },
		{ confidenceLevel: 2, responsiveDoc: [], nonResponsiveDoc: [] },
		{ confidenceLevel: 3, responsiveDoc: [], nonResponsiveDoc: [] },
		{ confidenceLevel: 4, responsiveDoc: [], nonResponsiveDoc: [] },
		{ confidenceLevel: 5, responsiveDoc: [], nonResponsiveDoc: [] },
		{ confidenceLevel: 6, responsiveDoc: [], nonResponsiveDoc: [] },
		{ confidenceLevel: 7, responsiveDoc: [], nonResponsiveDoc: [] },
		{ confidenceLevel: 8, responsiveDoc: [], nonResponsiveDoc: [] },
		{ confidenceLevel: 9, responsiveDoc: [], nonResponsiveDoc: [] },
		{ confidenceLevel: 10, responsiveDoc: [], nonResponsiveDoc: [] }
	];

	public totalDocCount: number = 0;

	private skip: number = 0;
	private readonly top: number = 100;

	public actualVsPredictedChartData: ChartSeries[] = [];
	public actualVsPredictedColorScheme = {
		domain: ['#93c47d', '#6d9eeb']
	};

	public confidenceOfPredictionsChartData: ChartSeries[] = [];
	public confidenceOfPredictionsColorScheme = {
		domain: ['#93c47d', '#6d9eeb']
	};

	public precisionPercentage: number;
	public precisionPercentageChartData: ChartValue[] = [];
	public precisionGraphColorScheme: any;

	public recallPercentage: number;
	public recallPercentageChartData: ChartValue[] = [];
	public recallGraphColorScheme: any;

	public pieChartGoodPercentageColorScheme = {
		domain: ['#5aa454', '#b7d4b4']
	};

	public pieChartBadPercentageColorScheme = {
		domain: ['#e44c24', '#eeb1a1']
	};

	constructor(private codingFieldsService: CodingFieldsService) {
		super();
		this.loadMultiDocGroupFields();
	}

	public selectCodingFieldChange(): void {
		this.isLoading = true;
		this.reset();
		this.getTextFieldsByFieldId()
			.pipe(expand(() => this.totalDocCount === -1 || this.totalDocCount > this.skip ?
				this.getTextFieldsByFieldId() :
				EMPTY))
			.subscribe(() => {
				if (this.totalDocCount <= this.skip) {
					this.loadChartActualVsPredicted();
					this.loadChartConfidenceOfPredictions();
					this.loadChartPrecision();
					this.loadChartRecall();

					this.isLoading = false;
				}
			});
	}

	public goToDocViewer(docList): void {
		const contextMap: DocViewerContext = {
			includeEmailFamiles: false,
			docList: docList
		};
		localStorage.setItem('ContextMapping', JSON.stringify(contextMap));
		this.openDocumentInDocumentViewer(docList[0].docId);
	}

	private loadMultiDocGroupFields(): void {
		this.codingFieldsService.getFields(false).pipe(
			map((data) => {
				const sections = data.value.filter(x => x.fieldId === x.parentFieldId);
				data.value
					.filter(x => x.fieldId !== x.parentFieldId && x.type === 1)
					.forEach((x) => {
						const field = {} as MultiDocField;
						field.displayTitle = x.displayTitle;
						field.fieldId = x.fieldId;
						field.rank = x.rank;
						field.fieldType = x.type;
						const parentField = sections.find((y) => y.fieldId === x.parentFieldId);
						if (this.codingFieldGroups.some(x => x.displayTitle === parentField.displayTitle)) {
							const group = this.codingFieldGroups.find(x => x.displayTitle === parentField.displayTitle);
							group.fields.push(field);
						}
						else {
							const group = {} as MultiDocGroup;
							group.displayTitle = parentField.displayTitle;
							group.fields = [];
							group.fields.push(field);
							this.codingFieldGroups.push(group);
						}
					});
			})).subscribe();
	}

	private getTextFieldsByFieldId(): Observable<any> {
		return this.codingFieldsService.getTextFieldsByFieldId(this.selectedCodingFieldId, this.top, this.skip)
			.pipe(map((response) => this.parseResults(response)));
	}

	private parseResults(response: PagingResponse<any>) {
		this.totalDocCount = response.count;
		this.skip += this.top;
		this.progress = Math.min(100, (this.skip * 100) / this.totalDocCount);

		const responsiveRegex = /^(Yes|No)\b/i;
		const confidenceRegex = /\b(Confidence Level:*\s*)(\d+)\b/i;
		response.value.forEach((data) => {
			const doc = this.mapDataToDoc(data);

			if (data.responsive === 1) {
				this.markedAsResponsiveHuman.push(doc);
			}
			else if (data.responsive === 2) {
				this.markedAsNonResponsiveHuman.push(doc);
			}
			else {
				this.notCodedHuman.push(doc);
			}

			if (responsiveRegex.test(data.text)) {
				const responsive = data.text.match(responsiveRegex)[0];
				if (responsive.length === 3) {
					this.markedAsResponsiveAI.push(doc);

					if (data.responsive === 1) {
						this.truePositive.push(doc);
					}
					else if (data.responsive === 2) {
						this.falsePositive.push(doc);
					}

					const confidence = data.text.match(confidenceRegex);
					if (confidence)
						this.confidenceList[Number(confidence[2]) - 1].responsiveDoc.push(doc);
				}
				else {
					this.markedAsNonResponsiveAI.push(doc);

					if (data.responsive === 1) {
						this.falseNegative.push(doc);
					}
					else if (data.responsive === 2) {
						this.trueNegative.push(doc);
					}

					const confidence = data.text.match(confidenceRegex);
					if (confidence)
						this.confidenceList[Number(confidence[2]) - 1].nonResponsiveDoc.push(doc);
				}
			}
			else {
				this.notCodedAI.push(data);
			}
		})

		this.precisionPercentage = this.truePositive.length * 100 / (this.truePositive.length + this.falsePositive.length);
		this.recallPercentage = this.truePositive.length * 100 / (this.truePositive.length + this.falseNegative.length);
	}

	private mapDataToDoc(data): DocViewerContextDoc {
		let displayType: 'native' | 'email' | 'attachment' = 'native';
		if (data.emailTypeId === EmailType.EmailAttachment.value) {
			displayType = 'attachment';
		}
		else if (data.emailTypeId === EmailType.EmailBody.value) {
			displayType = 'email';
		}
		const doc = new DocViewerContextDoc();
		doc.docId = data.docId;
		doc.docTitle = data.docTitle;
		doc.emailTypeId = data.emailTypeId;
		doc.displayType = displayType;

		return doc;
	}

	private reset() {
		this.progress = 0;
		this.skip = 0;
		this.totalDocCount = -1;
		this.truePositive = [];
		this.falsePositive = [];
		this.trueNegative = [];
		this.falseNegative = [];
		this.confidenceList = [
			{ confidenceLevel: 1, responsiveDoc: [], nonResponsiveDoc: [] },
			{ confidenceLevel: 2, responsiveDoc: [], nonResponsiveDoc: [] },
			{ confidenceLevel: 3, responsiveDoc: [], nonResponsiveDoc: [] },
			{ confidenceLevel: 4, responsiveDoc: [], nonResponsiveDoc: [] },
			{ confidenceLevel: 5, responsiveDoc: [], nonResponsiveDoc: [] },
			{ confidenceLevel: 6, responsiveDoc: [], nonResponsiveDoc: [] },
			{ confidenceLevel: 7, responsiveDoc: [], nonResponsiveDoc: [] },
			{ confidenceLevel: 8, responsiveDoc: [], nonResponsiveDoc: [] },
			{ confidenceLevel: 9, responsiveDoc: [], nonResponsiveDoc: [] },
			{ confidenceLevel: 10, responsiveDoc: [], nonResponsiveDoc: [] }
		];
		this.markedAsResponsiveHuman = [];
		this.markedAsNonResponsiveHuman = [];
		this.notCodedHuman = [];
		this.markedAsResponsiveAI = [];
		this.markedAsNonResponsiveAI = [];
		this.notCodedAI = [];

		this.actualVsPredictedChartData = [];
		this.confidenceOfPredictionsChartData = [];
		this.precisionPercentageChartData = [];
		this.recallPercentageChartData = [];
	}

	public selectActualOrPredictedDocuments(chartEvent) {
		if (chartEvent.name.toLowerCase() === "actual") {
			if (chartEvent.series.toLowerCase() === "responsive") {
				this.goToDocViewer(this.markedAsResponsiveHuman);
			}
			else {
				this.goToDocViewer(this.markedAsNonResponsiveHuman);
			}
		}
		else {
			if (chartEvent.series.toLowerCase() === "responsive") {
				this.goToDocViewer(this.markedAsResponsiveAI);
			}
			else {
				this.goToDocViewer(this.markedAsNonResponsiveAI);
			}
		}
	}

	public selectConfidenceOfPredictionsDocuments(chartEvent) {
		const index = this.confidenceList.findIndex(item => item.confidenceLevel.toString() === chartEvent.series);

		if (chartEvent.name.toLowerCase() === "responsive") {
			this.goToDocViewer(this.confidenceList[index].responsiveDoc);
		}
		else {
			this.goToDocViewer(this.confidenceList[index].nonResponsiveDoc);
		}
	}

	private loadChartActualVsPredicted() {
		this.actualVsPredictedChartData.push({
			name: "Responsive",
			series: [
				{
					name: "Actual",
					value: (this.markedAsResponsiveHuman.length).toString()
				},
				{
					name: "Predicted",
					value: (this.markedAsResponsiveAI.length).toString()
				}]
		});
		this.actualVsPredictedChartData.push({
			name: "Non-responsive",
			series: [
				{
					name: "Actual",
					value: (this.markedAsNonResponsiveHuman.length).toString()
				},
				{
					name: "Predicted",
					value: (this.markedAsNonResponsiveAI.length).toString()
				}]
		});
	}

	private loadChartConfidenceOfPredictions() {
		this.confidenceList.forEach(item => {
			this.confidenceOfPredictionsChartData.push({
				name: item.confidenceLevel.toString(),
				series: [
					{
						name: "Responsive",
						value: item.responsiveDoc.length.toString()
					},
					{
						name: "Non-responsive",
						value: item.nonResponsiveDoc.length.toString()
					}]
			});
		});
	}

	private loadChartPrecision() {
		this.precisionPercentageChartData.push({
			name: "Precision",
			value: this.precisionPercentage.toString()
		});

		this.precisionPercentageChartData.push({
			name: "Total",
			value: (100 - this.precisionPercentage).toString()
		});

		if (this.precisionPercentage > 60.0) {
			this.precisionGraphColorScheme = this.pieChartGoodPercentageColorScheme;
		}
		else {
			this.precisionGraphColorScheme = this.pieChartBadPercentageColorScheme;
		}
	}

	private loadChartRecall() {
		this.recallPercentageChartData.push({
			name: "Recall",
			value: this.recallPercentage.toString()
		});

		this.recallPercentageChartData.push({
			name: "Total",
			value: (100 - this.recallPercentage).toString()
		});

		if (this.recallPercentage > 75.0) {
			this.recallGraphColorScheme = this.pieChartGoodPercentageColorScheme;
		}
		else {
			this.recallGraphColorScheme = this.pieChartBadPercentageColorScheme;
		}
	}

	@HostListener('document:visibilitychange')
	visibilitychange() {
		this.focusFix.nativeElement.focus();
	}
}

interface ChartValue {
	name: string,
	value: string
}
interface ChartSeries {
	name: string,
	series: ChartValue[]
}

